// YouTube mode — right details panel (clone of ../components/DetailsPanel.tsx
// without the watch-list action).
//
//   title (h2)
//   channel · 12:34 · published 2021-05-05
//   watched 3× · first … · last …
//   category · language · views
//   [domain / subdomain] [domain] …
//   open on youtube ↗
//   description (collapsible, scrolls)
//   ─────────────────
//   on queue · unrated                ← current state line + ✕ remove
//   [0 not relevant][5 valuable]      ← 2×5 grid: 0–4 down left, 5–9 down right
//   [1 noise       ][6 reference] …
//
// Rate semantics: 1–9 → watched (relevance), 0 → ignore from any list.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";

import { apiJson, ApiError } from "../api/client";
import {
  fillQueue,
  fmtCount,
  fmtDate,
  fmtDuration,
  YT_RATING_LABELS,
  YT_ZERO_LABEL,
  ytKeys,
  type YtListItem,
  type YtListName,
  type YtVideoDetail,
} from "./api";

import "../components/DetailsPanel.css";
import "./youtube.css";

export interface YtRateModeState {
  position: number;
  total: number;
  onExit: () => void;
}

export function YoutubeDetails({
  videoId,
  video,
  error,
  isLoading,
  rateMode = null,
  siblings = [],
}: {
  videoId: string | null;
  video: YtVideoDetail | undefined;
  error: Error | undefined;
  isLoading: boolean;
  rateMode?: YtRateModeState | null;
  // Ordered items of the currently selected (source) list, so that after an
  // action the selection can advance to the next item in that list.
  siblings?: YtListItem[];
}) {
  if (rateMode && rateMode.total === 0 && !videoId) {
    return <EmptyQueue onExit={rateMode.onExit} />;
  }
  if (!videoId) {
    return <div className="details details--empty">no video selected</div>;
  }
  if (error) {
    const status = error instanceof ApiError ? error.status : null;
    return (
      <div className="details details--empty details--error">
        {status === 503 ? "youtube history database not found on the server" : "couldn't load video"}
      </div>
    );
  }
  if (isLoading || !video) {
    return <div className="details details--empty">loading…</div>;
  }
  return <VideoDetails key={video.video_id} video={video} rateMode={rateMode} siblings={siblings} />;
}

// ── "queue is empty" screen with an inline fill button ─────────────────────

function EmptyQueue({ onExit }: { onExit: () => void }) {
  const { mutate } = useSWRConfig();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function fill() {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      const res = await fillQueue();
      setNote(res.added === 0 ? "nothing left to rate — every video is on a list" : null);
      void mutate(ytKeys.lists);
      void mutate(ytKeys.list("queue"));
      void mutate(ytKeys.stats);
    } catch (e) {
      setNote(
        e instanceof ApiError && e.status === 503
          ? "youtube history database not found on the server"
          : "could not fill the queue",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="details details--empty">
      <div style={{ textAlign: "center" }}>
        <p>queue is empty — every video is rated.</p>
        <p className="details__hint">fill the queue to get the next 100 random videos.</p>
        {note && <p className="details__rate-error">{note}</p>}
        <div className="yt-empty-actions">
          <button
            type="button"
            className="rate-mode__exit rate-mode__exit--primary"
            disabled={busy}
            onClick={() => void fill()}
          >
            {busy ? "filling…" : "fill queue"}
          </button>
          <button type="button" className="rate-mode__exit" onClick={onExit}>
            done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── the panel proper ───────────────────────────────────────────────────────

const DESCRIPTION_PREVIEW = 320;

function VideoDetails({
  video,
  rateMode,
  siblings,
}: {
  video: YtVideoDetail;
  rateMode: YtRateModeState | null;
  siblings: YtListItem[];
}) {
  const { mutate } = useSWRConfig();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);
  const [descOpen, setDescOpen] = useState(false);

  // After an action, keep the cursor in the SOURCE list and advance to the next
  // item there (or the previous if this was last). Rate mode has its own queue
  // advancement in the Navigator, so it's left untouched here.
  function advanceInSource(prevList: YtListName | null, moved: boolean) {
    if (rateMode) return;
    const idx = siblings.findIndex((s) => s.video_id === video.video_id);
    const nextId =
      idx < 0
        ? null
        : idx + 1 < siblings.length
          ? siblings[idx + 1].video_id
          : idx - 1 >= 0
            ? siblings[idx - 1].video_id
            : null;

    const next = new URLSearchParams(params);
    if (prevList) next.set("list", prevList);
    if (nextId) {
      next.set("video", nextId);
    } else if (moved) {
      next.delete("video");
    } else {
      return;
    }
    setParams(next, { replace: true });
  }

  async function onRate(rating: number) {
    if (busy) return;
    setBusy(true);
    setRateError(null);
    const prevList = video.list;
    try {
      const updated = await apiJson<YtVideoDetail>(ytKeys.rate(video.video_id), {
        method: "POST",
        body: JSON.stringify({ rating }),
      });
      await mutate(ytKeys.video(video.video_id), updated, { revalidate: false });
      void mutate(ytKeys.lists);
      if (prevList) void mutate(ytKeys.list(prevList));
      const moved = !!updated.list && updated.list !== prevList;
      if (moved && updated.list) void mutate(ytKeys.list(updated.list));
      void mutate(ytKeys.stats);
      advanceInSource(prevList, moved);
    } catch {
      setRateError("rating failed — try again");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    if (busy || video.list == null) return;
    setBusy(true);
    setRateError(null);
    const prevList = video.list;
    try {
      const updated = await apiJson<YtVideoDetail>(ytKeys.remove(video.video_id), {
        method: "POST",
      });
      await mutate(ytKeys.video(video.video_id), updated, { revalidate: false });
      void mutate(ytKeys.lists);
      if (prevList) void mutate(ytKeys.list(prevList));
      void mutate(ytKeys.stats);
      advanceInSource(prevList, true); // it left every list
    } catch {
      setRateError("could not remove");
    } finally {
      setBusy(false);
    }
  }

  // Keyboard shortcuts: 0–9 rate — mirrors the buttons' disabled rules.
  // Ignored while typing in a field, with a modifier held, or while the
  // YouTube iframe owns focus (those keydowns never reach us anyway).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (busy) return;
      if (e.key >= "0" && e.key <= "9") {
        const n = Number(e.key);
        if (n === 0 && (video.list === "ignore" || video.list == null)) return;
        e.preventDefault();
        void onRate(n);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.video_id, video.list, busy]);

  const line1 = [
    video.channel,
    fmtDuration(video.duration_seconds),
    video.published_at ? `published ${fmtDate(video.published_at)}` : null,
  ].filter(Boolean);

  const line2 =
    video.watch_count > 0
      ? [
          `watched ${video.watch_count}×`,
          video.first_watched_at && video.watch_count > 1
            ? `first ${fmtDate(video.first_watched_at)}`
            : null,
          video.last_watched_at ? `last ${fmtDate(video.last_watched_at)}` : null,
        ].filter(Boolean)
      : [];

  const line3 = [
    video.category,
    video.language && video.language !== "zxx" ? video.language : null,
    video.view_count != null ? `${fmtCount(video.view_count)} views` : null,
  ].filter(Boolean);

  const description = video.description?.trim() ?? "";
  const longDesc = description.length > DESCRIPTION_PREVIEW;
  const shownDesc =
    longDesc && !descOpen ? description.slice(0, DESCRIPTION_PREVIEW).trimEnd() + "…" : description;

  return (
    <div className="details">
      <div className="details__scroll">
        <h2 className="details__title">{video.title}</h2>
        {line1.length > 0 && <div className="details__meta">{line1.join(" · ")}</div>}
        {line2.length > 0 && (
          <div className="yt-detail__row yt-detail__row--first">{line2.join(" · ")}</div>
        )}
        {line3.length > 0 && <div className="yt-detail__row">{line3.join(" · ")}</div>}

        {(video.domains.length > 0 || video.unavailable) && (
          <div className="details__genres">
            {video.unavailable && (
              <span className="details__genre-badge yt-detail__badge--warn">
                no longer on youtube
              </span>
            )}
            {video.domains.map((d, i) => {
              const label = d.subdomain ? `${d.domain} / ${d.subdomain}` : d.domain;
              return (
                <span key={`${label}-${i}`} className="details__genre-badge">
                  {label}
                </span>
              );
            })}
          </div>
        )}

        <a className="yt-detail__link" href={video.url} target="_blank" rel="noopener noreferrer">
          open on youtube ↗
        </a>

        {description && (
          <>
            <p className="details__synopsis yt-desc">{shownDesc}</p>
            {longDesc && (
              <button type="button" className="yt-desc__toggle" onClick={() => setDescOpen((o) => !o)}>
                {descOpen ? "less" : "more"}
              </button>
            )}
          </>
        )}
      </div>

      <div className="details__footer">
        {rateMode && (
          <div className="rate-mode" role="status">
            <span className="rate-mode__label">
              rating queue · {rateMode.position || "–"} of {rateMode.total}
            </span>
            <button
              type="button"
              className="rate-mode__exit rate-mode__exit--big"
              onClick={rateMode.onExit}
            >
              done
            </button>
          </div>
        )}
        <div className="details__state-row">
          <span className="details__state">{stateLine(video)}</span>
          <button
            type="button"
            className="remove-btn"
            disabled={busy || video.list == null}
            title="delete this video from all lists (it may be sampled again)"
            onClick={() => void onRemove()}
          >
            ✕ remove
          </button>
        </div>
        {rateError && (
          <div className="details__rate-error" role="alert">
            {rateError}
          </div>
        )}
        <RatingGrid list={video.list} rating={video.rating} busy={busy} onRate={onRate} />
      </div>
    </div>
  );
}

function stateLine(video: YtVideoDetail): string {
  const where = video.list ? `on ${video.list}` : "not on any list";
  if (video.rating != null) {
    const label = YT_RATING_LABELS[video.rating];
    return `${where} · rated ${video.rating}${label ? ` — ${label}` : ""}`;
  }
  return `${where} · unrated`;
}

// ── RatingGrid — 5×2 grid of labelled relevance buttons. 0 = "not relevant"
// → ignore (from queue or watched); disabled on ignore / off all lists.

function RatingGrid({
  list,
  rating,
  busy,
  onRate,
}: {
  list: YtListName | null;
  rating: number | null;
  busy: boolean;
  onRate: (rating: number) => void;
}) {
  const zeroDisabled = busy || list === "ignore" || list == null;
  const zeroTitle =
    list === "queue"
      ? "not relevant — move to ignore"
      : list === "watched"
        ? "not relevant — drop the rating and move to ignore"
        : YT_ZERO_LABEL;

  return (
    <div className="rating">
      <div className="rating__grid" role="group" aria-label="Rate this video">
        {Array.from({ length: 10 }, (_, n) => {
          const isZero = n === 0;
          const current = list === "watched" && rating === n && n > 0;
          const label = isZero ? YT_ZERO_LABEL : YT_RATING_LABELS[n];
          return (
            <button
              key={n}
              type="button"
              className={"rating__btn" + (current ? " rating__btn--current" : "")}
              disabled={isZero ? zeroDisabled : busy}
              title={isZero ? zeroTitle : label}
              aria-pressed={current}
              onClick={() => onRate(n)}
            >
              <span className="rating__num">{n}</span>
              <span className="rating__label">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
